import React, { useEffect, useState } from 'react';

import {
    GetRevenueAdjustmentTypesInput,
    GetRevenueAdjustmentTypesOutput,
    RevenueAdjustmentTypeDefinitionsEnum,
    RevenueAdjustmentTypeEnum,
    RevenueAdjustmentTypes,
    VpirBundleRevenueDeduction
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';

import { isUndefined } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import { Input } from 'src/components/Input';
import { revenueValidationRules } from 'src/components/RateCardDetail/ContractTerms/contractTermValidation';
import { Toggle } from 'src/components/Toggle';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { AppContext } from 'src/contexts/appContext';
import { IContractTermsComponentProps } from 'src/types/IRateCard';
import { hasErrorMessage } from 'src/util/validationsUtil';

export const Revenue = ({
    contractTerms,
    handleContractTermValueChange,
    setIsContractTermInputValid,
    isEditMode,
    handleContractTermErrorMessageChange,
    contractTermErrorMessages
}: IContractTermsComponentProps) => {
    const { setTopAlertMsg } = React.useContext(AppContext);
    const [bundleRevenueModels, setBundleRevenueModels] = useState<RevenueAdjustmentTypes>();
    const [bundleRevenueModelSelectionValue, setBundleRevenueModelSelectionValue] = useState<string | undefined>(
        !isUndefined(contractTerms.revenue?.vpirBundleRevenueDeduction)
            ? RevenueAdjustmentTypeEnum.AB_REVENUE_DEDUCTION_VPIR
            : undefined
    );

    const handleRevenueErrorMessageChange = (field: string, newVal: string) => {
        if (!handleContractTermErrorMessageChange) return;
        handleContractTermErrorMessageChange('revenue', field, newVal);
        if (!setIsContractTermInputValid) return;
    };

    const handleBundleRevenueToggle = (newVal: boolean) => {
        // Toggle on
        if (newVal) {
            // Currently only VPIR bundle model exists, so grab default values
            const bundleModel = bundleRevenueModels![0].revenueAdjustmentType!;
            setBundleRevenueModelSelectionValue(bundleModel);

            const revenueAdjustmentDefinitionsMap = new Map(
                bundleRevenueModels![0].revenueAdjustmentTypeDefinitions?.map((definition) => [
                    definition.type!,
                    definition.value!
                ])
            );
            const indDifferential1 = revenueAdjustmentDefinitionsMap.get(
                RevenueAdjustmentTypeDefinitionsEnum.IND_DIFFERENTIAL_1
            );
            const indDifferential2 = revenueAdjustmentDefinitionsMap.get(
                RevenueAdjustmentTypeDefinitionsEnum.IND_DIFFERENTIAL_2
            );
            const famDifferential1 = revenueAdjustmentDefinitionsMap.get(
                RevenueAdjustmentTypeDefinitionsEnum.FAM_DIFFERENTIAL_1
            );
            const famDifferential2 = revenueAdjustmentDefinitionsMap.get(
                RevenueAdjustmentTypeDefinitionsEnum.FAM_DIFFERENTIAL_2
            );

            handleContractTermValueChange(
                'revenue',
                'vpirBundleRevenueDeduction',
                VpirBundleRevenueDeduction.fromJson({
                    [RevenueAdjustmentTypeDefinitionsEnum.IND_DIFFERENTIAL_1]: indDifferential1,
                    [RevenueAdjustmentTypeDefinitionsEnum.IND_DIFFERENTIAL_2]: indDifferential2,
                    [RevenueAdjustmentTypeDefinitionsEnum.FAM_DIFFERENTIAL_1]: famDifferential1,
                    [RevenueAdjustmentTypeDefinitionsEnum.FAM_DIFFERENTIAL_2]: famDifferential2,
                    [RevenueAdjustmentTypeDefinitionsEnum.VENDOR_PARTICIPATION_1]: '0',
                    [RevenueAdjustmentTypeDefinitionsEnum.VENDOR_PARTICIPATION_2]: '0',
                    [RevenueAdjustmentTypeDefinitionsEnum.VENDOR_PARTICIPATION_3]: '0'
                })
            );
        }

        // Toggle off
        else {
            handleContractTermValueChange('revenue', 'vpirBundleRevenueDeduction', undefined);
        }
    };

    const handleBundleRevenueValuesChange = (newVal: string, type: RevenueAdjustmentTypeDefinitionsEnum) => {
        handleContractTermValueChange(
            'revenue',
            'vpirBundleRevenueDeduction',
            VpirBundleRevenueDeduction.fromJson({
                ...contractTerms.revenue?.vpirBundleRevenueDeduction,
                [type]: newVal
            })
        );
    };

    useEffect(() => {
        if (!isEditMode) {
            return;
        }

        contractConfigurationClient.getRevenueAdjustmentTypes(GetRevenueAdjustmentTypesInput.clone({})).subscribe(
            (response: GetRevenueAdjustmentTypesOutput) => {
                // Filter only bundle revenue adjustment model
                const bundleRevenueAdjustmentModels = response.revenueAdjustmentTypes?.filter(
                    (type) => type.revenueAdjustmentType === RevenueAdjustmentTypeEnum.AB_REVENUE_DEDUCTION_VPIR
                );
                setBundleRevenueModels(bundleRevenueAdjustmentModels);
                setBundleRevenueModelSelectionValue(bundleRevenueAdjustmentModels![0].revenueAdjustmentType!);
            },
            (error: UnknownCoralError) => {
                setTopAlertMsg(error.receivedException.message);
            }
        );
    }, []);

    useEffect(() => {
        if (!setIsContractTermInputValid) return;
        setIsContractTermInputValid((prevState) => ({
            ...prevState,
            revenue: !hasErrorMessage(contractTermErrorMessages!.revenue)
        }));
    }, [contractTermErrorMessages!.revenue]);

    const getStringValue = (key: RevenueAdjustmentTypeDefinitionsEnum, isEditMode = false): string =>
        isUndefined(contractTerms.revenue?.vpirBundleRevenueDeduction)
            ? '-'
            : isEditMode
              ? `${contractTerms.revenue?.vpirBundleRevenueDeduction[key] ?? 0}`
              : `${(contractTerms.revenue?.vpirBundleRevenueDeduction[key] ?? 0) * 100}%`;

    return (
        <Box className="box-border-light-gray" type="outline" spacingInset="400">
            <Column>
                <Row spacing="none">
                    <Heading level={3}>Revenue</Heading>
                    <ContractTermInfoButton contractTermName={ContractTermName.Revenue} />
                </Row>
                <Row alignmentVertical="top" widths="50%">
                    <Input
                        label="Revenue share percentage"
                        value={(contractTerms.revenue?.revSharePercent ?? '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        onChange={(newVal) => handleContractTermValueChange('revenue', 'revSharePercent', newVal)}
                        errorMessage={contractTermErrorMessages!.revenue.revSharePercent}
                        handleErrMsgChange={(newval) => handleRevenueErrorMessageChange('revSharePercent', newval)}
                        validationRules={revenueValidationRules}
                        type="number"
                    />
                    <Column>
                        <Toggle
                            label="Include bad debt on net revenue"
                            value={contractTerms.revenue!.includeBadDebt!}
                            isEditMode={isEditMode}
                            onChange={(newVal) => handleContractTermValueChange('revenue', 'includeBadDebt', newVal)}
                        />
                        <Toggle
                            label="Deduct chargebacks from net revenue"
                            value={contractTerms.revenue!.includeChargebacks!}
                            isEditMode={isEditMode}
                            onChange={(newVal) =>
                                handleContractTermValueChange('revenue', 'includeChargebacks', newVal)
                            }
                        />
                    </Column>
                </Row>
                <Row alignmentHorizontal="justify">
                    <Row spacing="none">
                        <Heading level={4}>Bundle revenue discounts</Heading>
                        <ContractTermInfoButton contractTermName={ContractTermName.BundleRevenueDiscount} />
                    </Row>

                    {isEditMode && (
                        <Toggle
                            label=""
                            value={!isUndefined(contractTerms.revenue?.vpirBundleRevenueDeduction)}
                            isEditMode={isEditMode}
                            onChange={handleBundleRevenueToggle}
                        />
                    )}
                </Row>
                {!isEditMode ? (
                    <Toggle
                        label="Vendor incremental participation level model 2024"
                        value={!isUndefined(contractTerms.revenue?.vpirBundleRevenueDeduction)}
                        isEditMode={isEditMode}
                    />
                ) : !isUndefined(contractTerms.revenue?.vpirBundleRevenueDeduction) ? (
                    <Select label="Select bundle model" value={bundleRevenueModelSelectionValue} size="xlarge">
                        {bundleRevenueModels?.map((model) => (
                            <SelectOption
                                key={model.revenueAdjustmentType!}
                                label={model.revenueAdjustmentType!}
                                value={model.revenueAdjustmentType!}
                            />
                        ))}
                    </Select>
                ) : (
                    <Toggle label="Enable bundle revenue discounts" value={false} isEditMode={false} />
                )}
                {!isUndefined(contractTerms.revenue?.vpirBundleRevenueDeduction) && (
                    <>
                        <Row widths="33%">
                            <Input
                                label="Ind differential 1"
                                value={getStringValue(
                                    RevenueAdjustmentTypeDefinitionsEnum.IND_DIFFERENTIAL_1,
                                    isEditMode
                                )}
                                isEditMode={false}
                            />
                            <Input
                                label="Ind differential 2"
                                value={getStringValue(
                                    RevenueAdjustmentTypeDefinitionsEnum.IND_DIFFERENTIAL_2,
                                    isEditMode
                                )}
                                isEditMode={false}
                            />
                        </Row>
                        <Row widths="33%">
                            <Input
                                label="Fam differential 1"
                                value={getStringValue(
                                    RevenueAdjustmentTypeDefinitionsEnum.FAM_DIFFERENTIAL_1,
                                    isEditMode
                                )}
                                isEditMode={false}
                            />
                            <Input
                                label="Fam differential 2"
                                value={getStringValue(
                                    RevenueAdjustmentTypeDefinitionsEnum.FAM_DIFFERENTIAL_2,
                                    isEditMode
                                )}
                                isEditMode={false}
                            />
                        </Row>
                        <Row widths="33%" alignmentVertical="top">
                            <Input
                                label="Vendor participation 1"
                                value={getStringValue(
                                    RevenueAdjustmentTypeDefinitionsEnum.VENDOR_PARTICIPATION_1,
                                    isEditMode
                                )}
                                placeholder="Enter decimal value"
                                onChange={(newVal) =>
                                    handleBundleRevenueValuesChange(
                                        newVal,
                                        RevenueAdjustmentTypeDefinitionsEnum.VENDOR_PARTICIPATION_1
                                    )
                                }
                                type="number"
                                isEditMode={isEditMode}
                                isRequired={true}
                                size="large"
                                errorMessage={contractTermErrorMessages!.revenue.vpirVendorParticipation1}
                                handleErrMsgChange={(newval) =>
                                    handleRevenueErrorMessageChange('vpirVendorParticipation1', newval)
                                }
                                validationRules={revenueValidationRules}
                            />
                            <Input
                                label="Vendor participation 2"
                                value={getStringValue(
                                    RevenueAdjustmentTypeDefinitionsEnum.VENDOR_PARTICIPATION_2,
                                    isEditMode
                                )}
                                placeholder="Enter decimal value"
                                onChange={(newVal) =>
                                    handleBundleRevenueValuesChange(
                                        newVal,
                                        RevenueAdjustmentTypeDefinitionsEnum.VENDOR_PARTICIPATION_2
                                    )
                                }
                                type="number"
                                isEditMode={isEditMode}
                                isRequired={true}
                                size="large"
                                errorMessage={contractTermErrorMessages!.revenue.vpirVendorParticipation2}
                                handleErrMsgChange={(newval) =>
                                    handleRevenueErrorMessageChange('vpirVendorParticipation2', newval)
                                }
                                validationRules={revenueValidationRules}
                            />
                            <Input
                                label="Vendor participation 3"
                                value={getStringValue(
                                    RevenueAdjustmentTypeDefinitionsEnum.VENDOR_PARTICIPATION_3,
                                    isEditMode
                                )}
                                placeholder="Enter decimal value"
                                onChange={(newVal) =>
                                    handleBundleRevenueValuesChange(
                                        newVal,
                                        RevenueAdjustmentTypeDefinitionsEnum.VENDOR_PARTICIPATION_3
                                    )
                                }
                                type="number"
                                isEditMode={isEditMode}
                                isRequired={true}
                                size="large"
                                errorMessage={contractTermErrorMessages!.revenue.vpirVendorParticipation3}
                                handleErrMsgChange={(newval) =>
                                    handleRevenueErrorMessageChange('vpirVendorParticipation3', newval)
                                }
                                validationRules={revenueValidationRules}
                            />
                        </Row>
                    </>
                )}
            </Column>
        </Box>
    );
};
